import React from 'react'
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import { currencySymbol, formatNumber } from '../lib/currency-formatter'
import * as Popper from 'popper.js'

import { AccountDetails } from '../types'
import { TradingAccount } from '@bdswiss/mt4-connector'
import AccountBalance from './AccountBalance'

import './AccountSwitcher.css'
import { Translate } from 'react-redux-i18n'
import HelpTooltip from './HelpTooltip'
import Spinner from './Spinner'
import { getAccountLabel, sendBackToDashboardPostMessage } from '../utils/common-utils'

export interface Props {
  accountLoaded: boolean
  accounts: AccountDetails[]
  activeId: string
  locale: string
  loginInfo: TradingAccount
  onSelect: (accountId: string, id: number) => void
  setLocale: (locale: string) => void
}

export interface State {
  dropdownOpen: boolean
}

class AccountSwitcher extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.toggle = this.toggle.bind(this)
    this.state = { dropdownOpen: false }

    props.setLocale(props.locale)
  }

  public render() {
    const { loginInfo, accounts, activeId, locale, onSelect } = this.props
    const account = accounts.find(item => item.login === activeId)!
    if (!account) {
      console.log('Loading accounts...')
      return <Spinner />
    }
    const { equity, margin, freeMargin } = loginInfo
    const marginLevel = margin ? (equity / margin) * 100 : 0
    const label = getAccountLabel(account, locale)
    const items: JSX.Element[] = []
    Object.keys(accounts).forEach((id, index) => {
      if (index !== 0) {
        items.push(<hr key={1000 + index} className="divider" />)
      }
      items.push(
        <DropdownItem key={index}>
          <AccountBalance
            account={accounts[parseInt(id, 10)]}
            loginInfo={loginInfo}
            locale={locale}
            onSelect={onSelect}
            active={accounts[parseInt(id, 10)].login === activeId}
            isFirst={index === 0}
          />
        </DropdownItem>
      )
    })
    const dropdownMenuModifiers = {
      setMaxHeight: {
        enabled: true,
        order: 890,
        fn: (data: Popper.Data) => {
          return {
            ...data,
            styles: {
              ...data.styles,
              position: 'relative',
              top: 'initial',
              left: 'initial',
              transform: 'translate3d(0, 0, 0)',
            },
          }
        },
      },
    }
    return (
      <div className="d-flex flex-row justify-content-end align-items-center">
        <div className="equity__subprime flex-column">
          <div className="d-flex">
            <Translate value={'equity'} />
            <HelpTooltip content="helpEquity" />
          </div>
          <div>
            <span data-cy="equity" className="equity__subprime__amount">
              {currencySymbol(account.currency)}{' '}
              {getNumberElement(formatNumber(equity, 2, account.currency))}
            </span>
          </div>
        </div>

        <div className="equity__subprime flex-column">
          <div className="d-flex">
            <Translate value={'freeMargin'} />
            <HelpTooltip content="helpFreeMargin" />
          </div>
          <div>
            <span className="equity__subprime__amount">
              {currencySymbol(account.currency)}{' '}
              {getNumberElement(formatNumber(freeMargin, 2, account.currency))}
            </span>
          </div>
        </div>

        <div className="equity__subprime flex-column">
          <div className="d-flex">
            <Translate value={'marginLevel'} />
            <HelpTooltip content="helpMarginLevel" />
          </div>
          <div>
            <span className="equity__subprime__amount">
              {marginLevel ? (
                <>{getNumberElement(formatNumber(marginLevel, 2))}%</>
              ) : (
                <span className="whole-part">-</span>
              )}
            </span>
          </div>
        </div>
        <Dropdown
          isOpen={this.state.dropdownOpen}
          toggle={this.toggle}
          className="equity"
          data-cy="accountsDropdown"
        >
          <DropdownToggle
            className={
              'equity__button switcher ' + (this.state.dropdownOpen ? '' : 'switcher-closed')
            }
            tag="div"
            caret={true}
          >
            <span>{label}</span>
            <div className="equity__button__amount">{account.login}</div>
          </DropdownToggle>
          <div
            className={
              'dropdown-menu-scrollable ' + (this.state.dropdownOpen ? '' : 'dropdown-menu-closed')
            }
          >
            <DropdownMenu modifiers={dropdownMenuModifiers} right={true} x-placement="bottom-end">
              {items}
              <div
                className="BackToDashboard d-flex justify-content-center text-primary cursor-pointer"
                onClick={sendBackToDashboardPostMessage}
              >
                <Translate value="backToDashboard" />
              </div>
            </DropdownMenu>
          </div>
        </Dropdown>
      </div>
    )
  }

  private toggle() {
    this.setState(prevState => ({
      dropdownOpen: !prevState.dropdownOpen,
    }))
  }
}

export function getNumberElement(figure: string) {
  const parts = figure.split('.')
  return (
    <>
      <span className="whole-part">{parts[0]}</span>
      {parts[1] ? `.${parts[1]}` : ''}
    </>
  )
}

export default AccountSwitcher
